import { useEffect, useMemo } from 'react'

import { useRequest } from 'ahooks'

import { GenericalRecord, KeySet } from '../../../../types/utilities'
import { toPageApi } from '../../../../utils/crud'
import { nextTick } from '../../../../utils/nextTick'
import { useRemoteTableStoreApi } from '../hooks/useStore'
import {
  RemoteTableAction,
  RemoteTableState,
  RemoteTableStore
} from '../store/interface'

import { RemoteTableStoreUpdaterProps } from './interface'

const DEFAULT_DATA = {
  list: [],
  current: 1,
  size: 20,
  total: 0,
  pages: 0
}

const useStoreUpdater = <
  T extends GenericalRecord,
  P extends GenericalRecord,
  K extends KeySet<RemoteTableState<T>>
>(
  key: K,
  value: RemoteTableStore<T, P>[K]
) => {
  const { setState } = useRemoteTableStoreApi()

  useEffect(() => {
    setState({ [key]: value })
  }, [key, value, setState])
}

const useStoreSetter = <
  T extends GenericalRecord,
  P extends GenericalRecord,
  K extends KeySet<RemoteTableAction<T, P>>
>(
  setter: K,
  value: any,
  shouldUpdate?: boolean
) => {
  const { getState } = useRemoteTableStoreApi()

  useEffect(() => {
    if (shouldUpdate) {
      getState()[setter](value)
    }
  }, [setter, value, shouldUpdate, getState])
}

export const RemoteTableStoreUpdater = <
  T extends GenericalRecord,
  P extends GenericalRecord
>({
  /* -------------------------------------------------------------------------- */
  /*                              RemoteTableState                              */
  /* -------------------------------------------------------------------------- */
  rowKey,
  containerRef,
  selectedRows,

  /* -------------------------------------------------------------------------- */
  /*                        RemoteTableStoreUpdater Props                       */
  /* -------------------------------------------------------------------------- */
  api,
  readyRequest,
  manualRequest,
  requestParams,
  refreshDeps,
  defaultCurrent = 1,
  defaultPageSize = 20
}: RemoteTableStoreUpdaterProps<T, P>) => {
  const { getState, setState } = useRemoteTableStoreApi()
  const mergedRequestParams = {
    ...requestParams,
    current: requestParams?.current ?? defaultCurrent,
    pageSize: requestParams?.pageSize ?? defaultPageSize
  }
  const {
    data = DEFAULT_DATA,
    error,
    loading,
    runAsync
  } = useRequest(
    toPageApi(api, {
      requestParams: mergedRequestParams as any
    }),
    {
      manual: manualRequest,
      ready: readyRequest,
      refreshDeps,
      refreshDepsAction() {
        // table props 变化导致的重新请求，将 requestParams 作为参数模拟手动请求
        getState().request(requestParams || {})
      },
      onFinally(params) {
        const { requestParams: oldRequestParams, setRequestParams } = getState()
        setRequestParams({
          ...oldRequestParams,
          ...mergedRequestParams,
          ...params[0]
        })
      },
      onError() {
        // 防止加载上一次参数的缓存数据
        nextTick(() => {
          setState({ data: [] })
        })
      }
    }
  )

  const pagination = useMemo(
    () => ({
      current: data.current,
      pageSize: data.size,
      total: data.total
    }),
    [data]
  )

  useStoreUpdater('data', data.list)
  useStoreUpdater('error', error)
  useStoreUpdater('loading', loading)
  useStoreUpdater('pagination', pagination)
  useStoreUpdater('_runAsync', runAsync)

  useStoreUpdater('rowKey', rowKey)
  useStoreUpdater('containerRef', containerRef)
  useStoreSetter('setSelectedRows', selectedRows, selectedRows !== undefined)

  return null
}
